import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dietService } from '../../services/dietService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';

const MEAL_OPTIONS: string[] = [
  'Breakfast',
  'Snack AM',
  'Lunch',
  'Side Dish Lunch',
  'Snack PM',
  'Dinner',
  'Side Dish Dinner',
  'Iftar Fruits',
  'Iftar Drinks',
  'Iftar Soup',
  'Iftar Appetizers',
  'Iftar Main Dish',
  'Iftar Salad',
  'Iftar Dessert',
  'Souhour',
];

const DAY_LABELS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];


function getCurrentWeekIndex(startingDate?: string, periodWeeks?: number): number | null {
  if (!startingDate || !periodWeeks || periodWeeks <= 0) return null;
  try {
    const start = new Date(startingDate + 'T00:00:00');
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 1; // future start -> show first
    const weekIndex = Math.floor(diffDays / 7) % periodWeeks; // 0-based
    return weekIndex + 1; // 1-based
  } catch { return null; }
}

const MenuDetailsViewPage: React.FC = () => {
  const { dietId } = useParams<{ dietId: string }>();
  const navigate = useNavigate();

  const { data: dietRes, isLoading: loadingDiet } = useQuery({
    queryKey: ['diet', dietId],
    queryFn: () => dietService.getDiet(dietId!),
    enabled: !!dietId,
  });

  const { data: menusRes, isLoading: loadingMenus } = useQuery({
    queryKey: ['menus', 'by-diet', dietId],
    queryFn: () => dietService.getMenus({ dietId, limit: 1000 }),
    enabled: !!dietId,
  });

  const diet = dietRes?.diet;
  const menus = menusRes?.menus || [];
  const menu = menus[0]; // assume a single menu per diet (choose first if many)

  const [schedule, setSchedule] = React.useState<Record<string, any[]>>({});
  const [weeksToShow, setWeeksToShow] = React.useState<Array<string>>([]);
  const [loadingSchedule, setLoadingSchedule] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Selection and inline details
  const [profileItemsCache, setProfileItemsCache] = React.useState<Record<string, any[]>>({});
  const [, setLoadingProfiles] = React.useState<Record<string, boolean>>({});

  // Ingredient caches (always-on; we prefetch and render directly)
  const [plateCache, setPlateCache] = React.useState<Record<string, any>>({});
  const [recipeCache, setRecipeCache] = React.useState<Record<string, any>>({});
  const [loadingPlate, setLoadingPlate] = React.useState<Record<string, boolean>>({});
  const [loadingRecipe, setLoadingRecipe] = React.useState<Record<string, boolean>>({});

  // Modal state for showing recipe/profile details
  const [showDetailsModal, setShowDetailsModal] = React.useState(false);
  const [selectedDetails, setSelectedDetails] = React.useState<{
    profileId?: string;
    plateId?: string;
    profileName?: string;
  } | null>(null);


  React.useEffect(() => {
    let ignore = false;
    async function loadSchedule() {
      if (!menu?.id) return;
      setLoadingSchedule(true);
      setError(null);
      try {
        // Find exactly which weeks/meals have data
        const avail = await dietService.getAvailableMenuDayPlates(menu.id);
        const combos: Array<{ week: number | null; mealType: string }> = (avail as any)?.items || [];

        const weeksSet = new Set<string>();
        const mealsByWeek = new Map<string, Set<string>>();
        for (const c of combos) {
          const w = c.week === null ? 'all' : String(c.week);
          if (c.week !== null) weeksSet.add(String(c.week));
          const set = mealsByWeek.get(w) || new Set<string>();
          set.add(String(c.mealType));
          mealsByWeek.set(w, set);
        }
        const weeks: string[] = Array.from(weeksSet).sort((a, b) => Number(a) - Number(b));

        // Build schedule: key `${week}__${meal}` -> items[]
        const sched: Record<string, any[]> = {};

        // Load 'all' only for meals that exist in 'all' combos
        const allMeals = mealsByWeek.get('all');
        if (allMeals && allMeals.size) {
          await Promise.all(
            Array.from(allMeals.values()).map(async (meal) => {
              const res = await dietService.getMenuDayPlates(menu.id, { week: 'all', meal });
              sched[`all__${meal}`] = (res as any)?.items || [];
            })
          );
        }

        // Load each real week only for meals that exist in that week
        await Promise.all(
          weeks.map(async (week) => {
            const ms = mealsByWeek.get(week) || new Set<string>();
            await Promise.all(
              Array.from(ms.values()).map(async (meal) => {
                const res = await dietService.getMenuDayPlates(menu.id, { week, meal });
                sched[`${week}__${meal}`] = (res as any)?.items || [];
              })
            );
          })
        );

        // Compute virtual weeks from 'All Weeks' rows when there are multiple rows
        let weeksToRender: string[] = [];
        const period = Number(diet?.periodWeeks || 0);

        if (weeks.length > 0) {
          // We have explicit weeks. Optionally extend using the max row count found in the base week (or 'all') up to periodWeeks
          const baseWeek = weeks[0];
          const baseMeals = new Set<string>(Array.from(mealsByWeek.get(baseWeek) || []));
          if (allMeals && allMeals.size) {
            for (const m of Array.from(allMeals.values())) baseMeals.add(m);
          }
          let maxRowsBase = 0;
          for (const meal of Array.from(baseMeals.values())) {
            const wkArr = sched[`${baseWeek}__${meal}`] || [];
            const allArr = sched[`all__${meal}`] || [];
            const baseArr = wkArr.length ? wkArr : allArr;
            if (!baseArr.length) continue;
            const s = new Set<number>();
            for (const it of baseArr) s.add(Number(it.sortOrder || 0));
            maxRowsBase = Math.max(maxRowsBase, s.size);
          }
          const baseCount = weeks.length;
          const target = Math.max(baseCount, maxRowsBase);
          const limit = period > 0 ? Math.min(target, period) : target;
          const set = new Set<string>(weeks);
          for (let i = 1; i <= limit; i++) set.add(String(i));
          weeksToRender = Array.from(set).sort((a, b) => Number(a) - Number(b));
        } else if (allMeals && allMeals.size) {
          // Only 'all' exists. If it has multiple rows, map rows to virtual weeks up to periodWeeks
          let maxRowsAll = 0;
          for (const meal of Array.from(allMeals.values())) {
            const arr = sched[`all__${meal}`] || [];
            const s = new Set<number>();
            for (const it of arr) s.add(Number(it.sortOrder || 0));
            maxRowsAll = Math.max(maxRowsAll, s.size);
          }
          if (maxRowsAll > 0) {
            const limit = period > 0 ? Math.min(maxRowsAll, period) : maxRowsAll;
            weeksToRender = Array.from({ length: limit }, (_, i) => String(i + 1));
          } else {
            weeksToRender = ['all'];
          }
        } else {
          weeksToRender = [];
        }

        if (!ignore) {
          setSchedule(sched);
          setWeeksToShow(weeksToRender);
        }
      } catch (e: any) {
        if (!ignore) setError(e?.message || 'Failed to load menu schedule');
      } finally {
        if (!ignore) setLoadingSchedule(false);
      }
    }
    loadSchedule();
    return () => { ignore = true; };
  }, [menu?.id]);

  // Prefetch profile items, plates, and recipes so we can render ingredients directly
  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    let ignore = false;
    async function prefetch() {
      const needProfiles = new Set<string>();
      const needPlates = new Set<string>();
      Object.values(schedule || {}).forEach((arr: any) => {
        (arr || []).forEach((it: any) => {
          const pid = it?.profileId || it?.profile_id;
          const plateId = it?.recipePlateId;
          if (pid && !profileItemsCache[String(pid)]) needProfiles.add(String(pid));
          if (!pid && plateId && !plateCache[String(plateId)]) needPlates.add(String(plateId));
        });
      });

      await Promise.all(Array.from(needProfiles).map(async (pid) => {
        setLoadingProfiles((p) => ({ ...p, [pid]: true }));
        try {
          const res = await dietService.getRecipePlateProfileItems(pid);
          const items = (res as any)?.items || [];
          if (!ignore) setProfileItemsCache((p) => ({ ...p, [pid]: items }));
          const recipeIds: string[] = Array.from(new Set(items.filter((x: any) => !!x.recipeId).map((x: any) => String(x.recipeId))));
          await Promise.all(recipeIds.map(async (rid) => {
            if (recipeCache[rid]) return;
            setLoadingRecipe((l) => ({ ...l, [rid]: true }));
            try {
              const rres = await dietService.getRecipe(rid);
              const recipe = (rres as any)?.recipe;
              if (!ignore) setRecipeCache((rc) => ({ ...rc, [rid]: recipe }));
            } finally {
              setLoadingRecipe((l) => ({ ...l, [rid]: false }));
            }
          }));
        } finally {
          setLoadingProfiles((p) => ({ ...p, [pid]: false }));
        }
      }));

      await Promise.all(Array.from(needPlates).map(async (plateId) => {
        setLoadingPlate((p) => ({ ...p, [plateId]: true }));
        try {
          const pres = await dietService.getRecipePlate(plateId);
          const plate = (pres as any)?.recipePlate;
          if (!ignore) setPlateCache((pc) => ({ ...pc, [plateId]: plate }));
          const rid = plate?.recipeId ? String(plate.recipeId) : undefined;
          if (rid && !recipeCache[rid]) {
            setLoadingRecipe((l) => ({ ...l, [rid]: true }));
            try {
              const rres = await dietService.getRecipe(rid);
              const recipe = (rres as any)?.recipe;
              if (!ignore) setRecipeCache((rc) => ({ ...rc, [rid]: recipe }));
            } finally {
              setLoadingRecipe((l) => ({ ...l, [rid]: false }));
            }
          }
        } catch {}
        finally {
          setLoadingPlate((p) => ({ ...p, [plateId]: false }));
        }
      }));
    }

    prefetch();
    return () => { ignore = true; };
  }, [schedule]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const currentWeek = getCurrentWeekIndex(diet?.startingDate, diet?.periodWeeks);

  const firstRealWeek = React.useMemo(() => {
    const weekNums: number[] = [];
    Object.keys(schedule || {}).forEach((k) => {
      const [w] = k.split('__');
      if (w && w !== 'all' && !isNaN(Number(w))) weekNums.push(Number(w));
    });
    if (!weekNums.length) return null;
    return Math.min(...weekNums);
  }, [schedule]);


  if (loadingDiet || loadingMenus || loadingSchedule) {
    return (
      <div className="flex items-center justify-center py-12"><LoadingSpinner size="lg" /></div>
    );
  }

  if (!diet) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Menu Details</h1>
          <Button variant="outline" onClick={() => navigate(-1)}>Back</Button>
        </div>
        <div className="text-red-600">Diet not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Diet Menu Details</h1>
          <p className="text-gray-600 mt-1">
            <span className="font-medium">Diet:</span> {diet?.name}
            {menu ? <span className="ml-3"><span className="font-medium">Menu:</span> {menu.name}</span> : null}
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/setup/diets')}>Back to Diets</Button>
          <Button variant="secondary" onClick={() => navigate(`/setup/menus/${menu?.id}/manage`)} disabled={!menu?.id}>Manage Menu</Button>
        </div>
      </div>

      {error && (
        <div className="rounded bg-yellow-50 border border-yellow-200 p-3 text-yellow-800">{error}</div>
      )}

      {weeksToShow.length === 0 && (
        <div className="rounded bg-gray-50 border border-gray-200 p-3 text-gray-700">No menu schedule found yet for this diet.</div>
      )}

      {/* Render one section per week */}
      {weeksToShow.map((week) => {
        const wNum = week === 'all' ? null : Number(week);
        const isCurrent = currentWeek && wNum === currentWeek;
        const bannerText = week === 'all' ? 'All Weeks' : `Week ${week}${isCurrent ? ' (Current Week)' : ''}`;
        return (
          <div key={week} className="bg-white rounded-lg shadow-soft overflow-hidden border border-gray-300">
            <div className="overflow-x-auto">
              <table className="table-auto min-w-[1200px] border-collapse w-full">
                <thead>
                  <tr>
                    <th colSpan={8} className={`px-4 py-3 text-left text-white font-semibold ${week==='all' ? 'bg-slate-500' : 'bg-lime-500'}`}>{bannerText}</th>
                  </tr>
                  <tr className="bg-gray-100 border-b-2 border-gray-400">
                    <th className="px-4 py-3 text-left font-bold text-gray-900 uppercase text-sm border-r-2 border-gray-400 w-32 bg-gray-50">Meal</th>
                    {DAY_LABELS.map((d) => (
                      <th key={d} className="px-4 py-3 text-center font-bold text-gray-900 uppercase text-sm border-r-2 border-gray-400 last:border-r-0">{d}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {MEAL_OPTIONS.map((meal) => {
                    // Per-week rendering with no fallback from week=all into numbered weeks
                    const itemsWeek = schedule[`${week}__${meal}`] || [];
                    const itemsAll = schedule[`all__${meal}`] || [];

                    // Decide source strictly:
                    // - For "all" section: use itemsAll
                    // - For numbered weeks: use only itemsWeek (do NOT fallback to itemsAll)
                    const merged = week === 'all' ? itemsAll : itemsWeek;

                    // hide empty meals (no items for this week/meal)
                    if (!merged.length) return null;

                    // compute rows from this week's data only
                    const sortOrders = new Set<number>();
                    merged.forEach((it) => sortOrders.add(Number(it.sortOrder || 0)));
                    const rowCount = Math.max(sortOrders.size || 0, 1);

                    // build a map: rowIndex -> day(1..7) -> { mdpId, plateId, label, profileId }
                    const rowMap: Record<number, Record<number, { mdpId?: string; plateId?: string; label: string; profileId?: string }>> = {} as any;
                    for (let ri = 0; ri < rowCount; ri++) rowMap[ri] = {} as any;
                    for (const it of merged) {
                      const ri = Number(it.sortOrder || 0);
                      const day = Number(it.day);
                      const label = it.profile_name || it.plate_name || it.recipe_name || '';
                      const entry = { mdpId: String(it.id || ''), plateId: String(it.recipePlateId || ''), label, profileId: it.profileId || it.profile_id } as any;
                      if (!rowMap[ri]) rowMap[ri] = {} as any;
                      // Week-specific should override "all"; since we concatenated [all, week], later items overwrite prior
                      rowMap[ri][day] = entry;
                    }

                    return (
                      <React.Fragment key={`${week}_${meal}`}>
                        {Array.from({ length: rowCount }).map((_, rIdx) => (
                          <tr key={`${meal}_${rIdx}`} className={`border-b-2 border-gray-300 ${rIdx % 2 === 1 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 transition-colors`}>
                            {rIdx === 0 && (
                              <td className="px-4 py-3 align-middle font-semibold text-gray-900 border-r-2 border-gray-400 bg-gray-100" rowSpan={rowCount}>{meal}</td>
                            )}
                            {DAY_LABELS.map((_, dIdx) => {
                              const cell = rowMap[rIdx]?.[dIdx + 1] as any;
                              const profileName = cell?.label || '';
                              const profileId = cell?.profileId;
                              const plateId = cell?.plateId;

                              const handleClick = () => {
                                if (profileId || plateId) {
                                  setSelectedDetails({
                                    profileId,
                                    plateId,
                                    profileName
                                  });
                                  setShowDetailsModal(true);
                                }
                              };

                              // Build content: show profile/plate name with checkbox
                              let content: React.ReactNode = <span className="text-gray-400 text-center block">-</span>;
                              if (profileName) {
                                content = (
                                  <div className="flex items-center justify-center text-sm gap-2">
                                    <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer" />
                                    <span
                                      className="text-primary-600 font-medium hover:underline cursor-pointer"
                                      onClick={handleClick}
                                    >
                                      {profileName}
                                    </span>
                                  </div>
                                );
                              }

                              return (
                                <td key={dIdx} className="px-4 py-3 text-sm text-gray-800 border-r-2 border-gray-300 last:border-r-0 text-center">
                                  {content}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Recipe/Profile Details Modal */}
      {showDetailsModal && selectedDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowDetailsModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-primary-600 text-white px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Recipe Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-white hover:text-gray-200 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              <div className="mb-4">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">{selectedDetails.profileName}</h4>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-900">Item Type</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-900">Recipe</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-900">Ingredients</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDetails.profileId && profileItemsCache[selectedDetails.profileId] ? (
                      (() => {
                        // Deduplicate items based on ingredient name or recipe ID
                        const items = profileItemsCache[selectedDetails.profileId];
                        const uniqueItems: any[] = [];
                        const seen = new Set<string>();

                        items.forEach((item: any) => {
                          const key = item.recipeId
                            ? `recipe_${item.recipeId}`
                            : `ingredient_${item.ingredient_name || item.ingredientId}`;

                          if (!seen.has(key)) {
                            seen.add(key);
                            uniqueItems.push(item);
                          }
                        });

                        return uniqueItems.map((item: any, idx: number) => {
                          const itemType = item.recipeId ? 'Recipe' : 'Ingredient';
                          const recipeName = item.recipeId ? (recipeCache[item.recipeId]?.name || 'Loading...') : '-';

                          let ingredients = '';
                          if (item.ingredient_name) {
                            ingredients = item.ingredient_name;
                          } else if (item.recipeId && recipeCache[item.recipeId]) {
                            const recipe = recipeCache[item.recipeId];
                            const ingredientNames = (recipe.recipeItems || [])
                              .map((ri: any) => ri?.ingredient?.name)
                              .filter(Boolean)
                              .join(', ');
                            ingredients = ingredientNames || 'No ingredients';
                          }

                          return (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="border border-gray-300 px-4 py-2 text-gray-800">{itemType}</td>
                              <td className="border border-gray-300 px-4 py-2 text-primary-600 font-medium">{recipeName}</td>
                              <td className="border border-gray-300 px-4 py-2 text-gray-800">{ingredients}</td>
                            </tr>
                          );
                        });
                      })()
                    ) : selectedDetails.plateId && plateCache[selectedDetails.plateId] ? (
                      (() => {
                        const plate = plateCache[selectedDetails.plateId];
                        const itemType = plate.recipeId ? 'Recipe' : 'Ingredient';
                        const recipeName = plate.recipeId ? (recipeCache[plate.recipeId]?.name || plate.recipe_name || 'Loading...') : '-';

                        let ingredients = '';
                        if (plate.ingredient) {
                          ingredients = plate.ingredient.name;
                        } else if (plate.recipeId && recipeCache[plate.recipeId]) {
                          const recipe = recipeCache[plate.recipeId];
                          const ingredientNames = (recipe.recipeItems || [])
                            .map((ri: any) => ri?.ingredient?.name)
                            .filter(Boolean)
                            .join(', ');
                          ingredients = ingredientNames || 'No ingredients';
                        }

                        return (
                          <tr className="bg-white">
                            <td className="border border-gray-300 px-4 py-2 text-gray-800">{itemType}</td>
                            <td className="border border-gray-300 px-4 py-2 text-primary-600 font-medium">{recipeName}</td>
                            <td className="border border-gray-300 px-4 py-2 text-gray-800">{ingredients}</td>
                          </tr>
                        );
                      })()
                    ) : (
                      <tr>
                        <td colSpan={3} className="border border-gray-300 px-4 py-2 text-center text-gray-500">
                          Loading details...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuDetailsViewPage;

